import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Sodo, Party } from '../../shared/interfaces';
import apiService from '../../services/ApiService';
import { apiRoutes } from '../../shared/apiRoutes';
import MatchResultsDialog from './MatchResultsDialog';
import DialogBox from '../common/DialogBox';
import Button from '../common/Button';
import { ExclamationTriangleIcon, TrophyIcon } from '@heroicons/react/24/outline';
import { normalizeSodaApiListResponse, mergeSodasCreated, mergeSodasUpdated } from '../../utils/gameBoardSodaMerge';
import { isCashParty, isCashPartyCode } from '../../utils/cashParty';

type SodaFieldKey = 'lk' | 'amount' | 'rate' | 'party';
const DEFAULT_FIELD_SEQUENCE: SodaFieldKey[] = ['lk', 'amount', 'rate', 'party'];

interface CharacterInputProps {
    onComplete: (sodas: Sodo[]) => void;
    validFirsChar: string[];
    gameId: string;
    parties: Party[];
    gameType: string;
    teamA?: string;
    teamB?: string;
    teamC?: string;
    initialToTeamMap: Record<string, string>;
    partnershipEnabled: boolean;
    commissionEnabled: boolean;
    onMatchOver?: () => void; // Optional callback for match over
    isGameOver?: boolean; // Optional flag to show/hide match over button
    gameDetails?: any; // Game details for match results
    editingSoda?: Sodo | null; // Soda being edited
    onEditComplete?: () => void; // Callback when edit is complete
    editOnly?: boolean; // When true, only allow edit mode (no new soda create)
}

/** Maximum amount accepted in Khadha (inclusive). */
const MAX_AMOUNT_INPUT = 99999.99;

const MAX_AMOUNT_FRACTION_DIGITS = 2;

function previewAmountInputAfterBeforeInput(
  el: HTMLInputElement,
  inputType: string,
  data: string | null,
): string | null {
  const v = el.value;
  const start = el.selectionStart ?? v.length;
  const end = el.selectionEnd ?? v.length;

  switch (inputType) {
    case 'insertText':
    case 'insertCompositionText':
    case 'insertReplacementText':
      if (data == null) return null;
      return v.slice(0, start) + data + v.slice(end);
    case 'insertFromPaste':
      if (data == null) return null;
      return v.slice(0, start) + data + v.slice(end);
    case 'deleteContentBackward': {
      if (start === 0 && start === end) return v;
      const delStart = start === end ? Math.max(0, start - 1) : start;
      return v.slice(0, delStart) + v.slice(end);
    }
    case 'deleteContentForward': {
      if (start >= v.length) return v;
      const delEnd = start === end ? Math.min(v.length, start + 1) : end;
      return v.slice(0, start) + v.slice(delEnd);
    }
    default:
      return null;
  }
}

function amountStringWouldExceedMax(candidate: string, max: number): boolean {
  const t = candidate.trim();
  if (t === '' || t === '-' || t === '.') return false;
  if (/\.$/.test(t)) {
    const w = Number(t.slice(0, -1));
    return Number.isFinite(w) && w > max;
  }
  const n = Number(t);
  if (!Number.isFinite(n)) return false;
  return n > max;
}

/** Returns digits + one decimal; at most two fractional digits. Does not coerce to MAX (reject/handle callers). */
function sanitizeKhadhaAmountString(raw: string): string {
  if (raw === '') return '';

  let s = raw.replace(/[^\d.]/g, '');
  if (!s) return '';

  const trailingDotIntent = /\.$/.test(s);
  const firstDot = s.indexOf('.');
  let intSide: string;
  let fracSide: string;

  if (firstDot === -1) {
    intSide = s;
    fracSide = '';
  } else {
    intSide = s.slice(0, firstDot);
    fracSide = s.slice(firstDot + 1).replace(/\./g, '');
    if (fracSide.length > MAX_AMOUNT_FRACTION_DIGITS) {
      fracSide = fracSide.slice(0, MAX_AMOUNT_FRACTION_DIGITS);
    }
  }

  const out =
    fracSide !== ''
      ? `${intSide}.${fracSide}`
      : trailingDotIntent && firstDot !== -1
        ? `${intSide}.`
        : intSide;

  return out;
}

const LagayaaKhadhaComponent: React.FC<CharacterInputProps> = ({validFirsChar, onComplete, gameId, parties, gameType, teamA, teamB, teamC, initialToTeamMap, partnershipEnabled, commissionEnabled, onMatchOver, isGameOver, gameDetails, editingSoda, onEditComplete, editOnly = false }) => {
  const [firstChar, setFirstChar] = useState('');
  const [thirdChar, setThirdChar] = useState('');
  const [editingSodaId, setEditingSodaId] = useState<number | null>(null);
  const lkInputRef = useRef<HTMLInputElement>(null);
  const rateInputRef = useRef<HTMLInputElement>(null);
  const amountInputRef = useRef<HTMLInputElement>(null);
  /** Last amount string that stayed within MAX_AMOUNT_INPUT — used when a keystroke/paste slips past beforeinput. */
  const amountLastWithinMaxRef = useRef('');
  const partyInputRef = useRef<HTMLInputElement>(null);
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  /** Prevents overlapping saveSodo runs from Enter bursts or queued timeouts (same record/edit session). */
  const saveInFlightRef = useRef(false);
  const partyEnterFlushTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isSavingSoda, setIsSavingSoda] = useState(false);
  const [partyCode, setPartyCode] = useState<string>(''); // Current typed value
  const [validatedPartyCode, setValidatedPartyCode] = useState<string>(''); // Original validated code
  const [partyValidation, setPartyValidation] = useState<{ isValid: boolean; error: string; partyName: string; partyId: number | null }>({
    isValid: false,
    error: '',
    partyName: '',
    partyId: null
  });
  const [message, setMessage] = useState<string>('');
  const [showMatchResults, setShowMatchResults] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  
  const [fieldSequence, setFieldSequence] = useState<SodaFieldKey[]>(DEFAULT_FIELD_SEQUENCE);

  const validSecondChar = ['K', 'L'] ;

  useEffect(() => {
    const fetchSequence = async () => {
      try {
        const data = await apiService.get<{ sequence: string[] }>(apiRoutes.settings.sodaFormSequence);
        if (data?.sequence && Array.isArray(data.sequence) && data.sequence.length === 4) {
          const validKeys = new Set<SodaFieldKey>(DEFAULT_FIELD_SEQUENCE);
          const fetched = data.sequence.filter((k: string) => validKeys.has(k as SodaFieldKey)) as SodaFieldKey[];
          if (fetched.length === 4) setFieldSequence(fetched);
        }
      } catch { /* use default */ }
    };
    fetchSequence();

    const onSequenceChanged = (e: Event) => {
      const seq = (e as CustomEvent).detail?.sequence;
      if (Array.isArray(seq) && seq.length === 4) setFieldSequence(seq as SodaFieldKey[]);
    };
    window.addEventListener('soda-form-sequence-changed', onSequenceChanged);
    return () => window.removeEventListener('soda-form-sequence-changed', onSequenceChanged);
  }, []);

  const getFieldRef = useCallback((key: SodaFieldKey): React.RefObject<HTMLInputElement | null> => {
    switch (key) {
      case 'lk': return lkInputRef;
      case 'amount': return amountInputRef;
      case 'rate': return rateInputRef;
      case 'party': return partyInputRef;
    }
  }, []);

  const getNextFieldRef = useCallback((currentField: SodaFieldKey): HTMLInputElement | HTMLButtonElement | null => {
    const idx = fieldSequence.indexOf(currentField);
    if (idx === -1 || idx === fieldSequence.length - 1) return saveButtonRef.current;
    const nextKey = fieldSequence[idx + 1];
    return getFieldRef(nextKey)?.current ?? null;
  }, [fieldSequence, getFieldRef]);

  const getFirstFieldRef = useCallback((): React.RefObject<HTMLInputElement | null> => {
    return getFieldRef(fieldSequence[0]);
  }, [fieldSequence, getFieldRef]);

  /**
   * Gets the text color class based on the second character (K or L)
   * @param secondChar - The second character (K or L)
   * @returns The CSS class for text color
   */
  const getTextColorClass = (secondChar: string): string => {
    if (secondChar === 'K') {
      return 'text-red-600'; // Red for K transactions
    } else if (secondChar === 'L') {
      return 'text-green-600'; // Green for L transactions
    }
    return 'text-black'; // Default black for other cases
  };

  // Focus the first input by default when component mounts
  useEffect(() => {
    getFirstFieldRef().current?.focus();
  }, [initialToTeamMap, getFirstFieldRef]);

  useEffect(() => {
    return () => {
      if (partyEnterFlushTimeoutRef.current) {
        clearTimeout(partyEnterFlushTimeoutRef.current);
      }
    };
  }, []);

  // Listen for custom event to set LK value from keyboard shortcuts
  useEffect(() => {
    const handleSetLkValue = (event: Event) => {
      const customEvent = event as CustomEvent<{ value: string }>;
      const value = customEvent.detail?.value;
      if (value && lkInputRef.current) {
        // Process the value the same way handleChange does
        const raw = value.toUpperCase().replace('/', '');
        let first = raw[0] || '';
        let third = raw[1] || '';

        // Validate first char - only validate if validFirsChar has values
        if (first && validFirsChar.length > 0 && !validFirsChar.includes(first)) {
          console.warn('Invalid first char:', first, 'valid chars:', validFirsChar);
          return;
        }

        // Validate second char (K or L)
        if (third && !['K', 'L'].includes(third)) {
          console.warn('Invalid second char:', third);
          return;
        }
        // Set the state values
        setFirstChar(first);
        setThirdChar(third);

        // If both characters are set, move focus to amount input
        if (first && third) {
          setTimeout(() => {
            amountInputRef.current?.focus();
          }, 10);
        }
      }
    };

    window.addEventListener('set-lk-value', handleSetLkValue);
    return () => {
      window.removeEventListener('set-lk-value', handleSetLkValue);
    };
  }, [validFirsChar]); // validFirsChar is used for validation

  // Add focus handler to party input ref
  useEffect(() => {
    if (partyInputRef.current) {
      const handlePartyFocus = () => handleFocus('party');
      partyInputRef.current.addEventListener('focus', handlePartyFocus);
      return () => {
        partyInputRef.current?.removeEventListener('focus', handlePartyFocus);
      };
    }
  }, []);

  // Validate party code format (same as CreateParty.tsx)
  const validatePartyCodeFormat = (code: string): { isValid: boolean; error: string } => {
    if (!code || code.trim().length === 0) {
      return { isValid: false, error: 'Party code is required' };
    }
    if (code.length < 1) {
      return { isValid: false, error: 'Party code must be 1 characters' };
    }
    if (code.length > 5) {
      return { isValid: false, error: 'Party code must be 5 characters' };
    }
    if (!/^[a-zA-Z0-9]+$/.test(code)) {
      return { isValid: false, error: 'Party code must be alphanumeric' };
    }
    return { isValid: true, error: '' };
  };

  // Validate and fetch party by code
  const validateAndFetchParty = async (code: string): Promise<{ isValid: boolean; error: string; partyName: string; partyId: number | null }> => {
    // Extract code part if user pasted "CODE - NAME" format
    const codeMatch = code.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const trimmedCode = codeMatch ? codeMatch[1].toUpperCase().trim() : code.trim().toUpperCase();
    
    // First validate format
    const formatValidation = validatePartyCodeFormat(trimmedCode);
    if (!formatValidation.isValid) {
      const validationResult = {
        isValid: false,
        error: formatValidation.error,
        partyName: '',
        partyId: null
      };
      setPartyValidation(validationResult);
      setValidatedPartyCode(''); // Clear validated code
      return validationResult;
    }

    if (isCashPartyCode(trimmedCode)) {
      const validationResult = {
        isValid: false,
        error: 'Cash party cannot be used for game soda',
        partyName: '',
        partyId: null
      };
      setPartyValidation(validationResult);
      setValidatedPartyCode('');
      return validationResult;
    }

    // Check if party exists in local parties list first
    const filteredParties = (parties || []).filter(p => !isCashParty(p));
    const foundParty = filteredParties.find(p => p.partyCode.toUpperCase() === trimmedCode);
    
    if (foundParty) {
      const validationResult = {
        isValid: true,
        error: '',
        partyName: foundParty.partyName,
        partyId: foundParty.id
      };
      setPartyValidation(validationResult);
      setValidatedPartyCode(trimmedCode); // Store validated code
      // Update input to show "CODE - NAME"
      if (partyInputRef.current) {
        partyInputRef.current.value = `${foundParty.partyCode} - ${foundParty.partyName}`;
      }
      setPartyCode(trimmedCode); // Update state with validated code
      return validationResult;
    }

    // If not found locally, try to fetch from API
    try {
      const partyData = await apiService.get(apiRoutes.parties.getByCode(trimmedCode));
      if (partyData && partyData.id && !isCashParty(partyData)) {
        const validationResult = {
          isValid: true,
          error: '',
          partyName: partyData.partyName || '',
          partyId: partyData.id
        };
        setPartyValidation(validationResult);
        setValidatedPartyCode(trimmedCode); // Store validated code
        // Update input to show "CODE - NAME"
        if (partyInputRef.current) {
          partyInputRef.current.value = `${partyData.partyCode} - ${partyData.partyName}`;
        }
        setPartyCode(trimmedCode); // Update state with validated code
        return validationResult;
      } else {
        const validationResult = {
          isValid: false,
          error: 'Invalid party code',
          partyName: '',
          partyId: null
        };
        setPartyValidation(validationResult);
        setValidatedPartyCode(''); // Clear validated code
        return validationResult;
      }
    } catch (error) {
      const validationResult = {
        isValid: false,
        error: 'Invalid party code',
        partyName: '',
        partyId: null
      };
      setPartyValidation(validationResult);
      setValidatedPartyCode(''); // Clear validated code
      return validationResult;
    }
  };

  // Handle party code input change
  const handlePartyCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Extract code part if user pasted "CODE - NAME" format, otherwise use the typed value
    const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const codeOnly = codeMatch ? codeMatch[1].toUpperCase() : value.toUpperCase();
    
    // Check if user is typing a new value (different from validated code)
    const isNewValue = codeOnly !== validatedPartyCode;
    
    if (isNewValue) {
      // User is typing a new value, clear validation and show only the code
      setPartyCode(codeOnly);
      setValidatedPartyCode(''); // Clear validated code
      setPartyValidation({
        isValid: false,
        error: '',
        partyName: '',
        partyId: null
      });
      // Update input to show only the code (remove "CODE - NAME" format if present)
      if (partyInputRef.current && value !== codeOnly) {
        partyInputRef.current.value = codeOnly;
      }
    } else {
      // User hasn't changed the validated code, keep the display format
      setPartyCode(codeOnly);
    }
  };

  // Handle party code blur
  const handlePartyCodeBlur = async () => {
    const value = partyInputRef.current?.value || partyCode;
    // Extract code part if value contains "CODE - NAME" format
    const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const codeToValidate = codeMatch ? codeMatch[1].toUpperCase().trim() : value.trim().toUpperCase();
    
    if (codeToValidate) {
      // Only validate if it's a new value (different from validated code)
      if (codeToValidate !== validatedPartyCode) {
        await validateAndFetchParty(codeToValidate);
      }
    } else {
      setPartyValidation({
        isValid: false,
        error: '',
        partyName: '',
        partyId: null
      });
      setValidatedPartyCode('');
      if (partyInputRef.current) {
        partyInputRef.current.value = '';
      }
    }
  };

  // Check if all fields are valid for saving
  const areAllFieldsValid = (): boolean => {
    const amount = Number(amountInputRef.current?.value) || 0;
    const rate = Number(rateInputRef.current?.value) || 0;
    
    // Check amount and rate
    if (!amount || !rate || amount > MAX_AMOUNT_INPUT || !Number.isFinite(amount)) {
      return false;
    }
    
    // Check LK (firstChar and thirdChar)
    const gt = gameType?.toLowerCase();
    if (gt === 'session') {
      // For session, firstChar should be a digit
      const digit = firstChar.replace(/[^0-9]/g, '');
      if (!digit) {
        return false;
      }
    } else {
      // For oneday/test, need both firstChar and thirdChar
      if (!firstChar || !thirdChar) {
        return false;
      }
    }
    
    // Check party validation
    if (!partyValidation.isValid || !partyValidation.partyId) {
      return false;
    }
    
    return true;
  };

  // Handle party code Enter key
  const handlePartyCodeEnter = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (saveInFlightRef.current) {
        return;
      }
      if (partyEnterFlushTimeoutRef.current) {
        clearTimeout(partyEnterFlushTimeoutRef.current);
        partyEnterFlushTimeoutRef.current = null;
      }
      const value = partyInputRef.current?.value || partyCode;
      // Extract code part if value contains "CODE - NAME" format
      const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
      const codeToValidate = codeMatch ? codeMatch[1].toUpperCase().trim() : value.trim().toUpperCase();
      
      if (codeToValidate) {
        // Only validate if it's a new value (different from validated code)
        let validationResult;
        if (codeToValidate !== validatedPartyCode) {
          validationResult = await validateAndFetchParty(codeToValidate);
        } else {
          // Use existing validation
          validationResult = {
            isValid: partyValidation.isValid,
            error: partyValidation.error,
            partyName: partyValidation.partyName,
            partyId: partyValidation.partyId
          };
        }
        
        if (validationResult.isValid && validationResult.partyId) {
          const isPartyLast = fieldSequence[fieldSequence.length - 1] === 'party';

          if (!isPartyLast) {
            const nextRef = getNextFieldRef('party');
            if (nextRef && typeof (nextRef as any).focus === 'function') {
              (nextRef as any).focus();
            }
            return;
          }

          partyEnterFlushTimeoutRef.current = setTimeout(() => {
            partyEnterFlushTimeoutRef.current = null;
            if (saveInFlightRef.current) {
              return;
            }
            if (areAllFieldsValid()) {
              void saveSodo();
            } else {
              const amount = Number(amountInputRef.current?.value) || 0;
              const rate = Number(rateInputRef.current?.value) || 0;
              
              if (!amount) {
                amountInputRef.current?.focus();
                setMessage('Please enter amount');
              } else if (amount > MAX_AMOUNT_INPUT || !Number.isFinite(amount)) {
                amountInputRef.current?.focus();
                setMessage(`Maximum amount is ${MAX_AMOUNT_INPUT.toLocaleString(undefined, { maximumFractionDigits: 2 })}`);
              } else if (!rate) {
                rateInputRef.current?.focus();
                setMessage('Please enter rate');
              } else if (!firstChar || (gameType?.toLowerCase() !== 'session' && !thirdChar)) {
                lkInputRef.current?.focus();
                setMessage('Please enter valid LK value');
              } else {
                void saveSodo();
              }
            }
          }, 50);
        } else {
          // Invalid party, stay in field and show error
          partyInputRef.current?.focus();
          setMessage(validationResult.error || 'Invalid party code');
        }
      } else {
        // Empty value, stay in field
        partyInputRef.current?.focus();
        setMessage('Party code is required');
      }
    } else {
      // For other keys (like Tab), let them propagate normally
      handleKeyDown(e, saveButtonRef.current!);
    }
  };

  // Populate form when editingSoda changes
  useEffect(() => {
    if (editingSoda) {
      setEditingSodaId(editingSoda.id);
      
      // Parse LK to get firstChar and thirdChar
      const lkUpper = editingSoda.LK.toUpperCase();
      const gt = gameType?.toLowerCase();
      
      if (gt === 'session') {
        // For session, LK is a digit
        setFirstChar(lkUpper);
        setThirdChar('');
      } else {
        // For oneday/test, need to extract from team name and LK
        // Find the initial from team name
        const teamInitial = Object.keys(initialToTeamMap).find(
          key => initialToTeamMap[key] === editingSoda.team
        );
        if (teamInitial) {
          setFirstChar(teamInitial);
        }
        // LK should be K or L
        if (lkUpper === 'K' || lkUpper === 'L') {
          setThirdChar(lkUpper);
        }
      }
      
      // Set amount and rate
      if (amountInputRef.current) {
        amountInputRef.current.value = editingSoda.amount.toString();
        amountLastWithinMaxRef.current = amountInputRef.current.value;
      }
      if (rateInputRef.current) {
        rateInputRef.current.value = editingSoda.rate.toString();
      }
      
      // Party: prefer API party id — do not use startsWith(partyCode): "77 - …".startsWith("7") matches code "7" before "77"
      setPartyValidation({ isValid: false, error: '', partyName: '', partyId: null });
      setPartyCode('');
      setValidatedPartyCode('');

      const pid = editingSoda.partyId;
      const foundById =
        pid != null && !Number.isNaN(Number(pid))
          ? parties.find((p) => p.id === Number(pid))
          : undefined;

      if (foundById) {
        setPartyCode(foundById.partyCode);
        setValidatedPartyCode(foundById.partyCode.toUpperCase());
        setPartyValidation({
          isValid: true,
          error: '',
          partyName: foundById.partyName,
          partyId: foundById.id,
        });
        if (partyInputRef.current) {
          partyInputRef.current.value = `${foundById.partyCode} - ${foundById.partyName}`;
        }
      } else if (editingSoda.party && editingSoda.party.includes(' - ')) {
        const codeOnly = editingSoda.party.split(' - ')[0].trim();
        const matchingParty = parties.find(
          (p) => p.partyCode.toUpperCase() === codeOnly.toUpperCase()
        );
        if (matchingParty) {
          setPartyCode(matchingParty.partyCode);
          setValidatedPartyCode(matchingParty.partyCode.toUpperCase());
          setPartyValidation({
            isValid: true,
            error: '',
            partyName: matchingParty.partyName,
            partyId: matchingParty.id,
          });
          if (partyInputRef.current) {
            partyInputRef.current.value = `${matchingParty.partyCode} - ${matchingParty.partyName}`;
          }
        }
      }
      
      setMessage('Editing soda - modify values and click Save');
      // Focus on amount input
      setTimeout(() => amountInputRef.current?.focus(), 100);
    } else {
      // Clear edit mode
      setEditingSodaId(null);
    }
  }, [editingSoda, parties, gameType, initialToTeamMap]);

  // Publish this bar's height as a CSS variable so layouts can size around it
  useEffect(() => {
    const updateHeightVar = () => {
      let h = barRef.current?.getBoundingClientRect().height || 64;
      document.documentElement.style.setProperty('--khadha-height', `${Math.ceil(h)}px`);
    };
    updateHeightVar();
    window.addEventListener('resize', updateHeightVar);
    return () => window.removeEventListener('resize', updateHeightVar);
  }, []);

  const getAvailablePartyCodes = () => parties.filter(p => !isCashParty(p)).map(party => party.partyCode).join(', ');

  const resolveTeam = (initial: string): string | null => {
    const ch = initial?.toUpperCase?.() || '';
    if (!ch) return null;
    // use mapping provided by GameBoard (built from pickInitial rules)
    if (initialToTeamMap && initialToTeamMap[ch]) return initialToTeamMap[ch];
    
    return null;
  }

  const handleAmountBeforeInput = (e: React.FormEvent<HTMLInputElement>) => {
    const ne = e.nativeEvent as InputEvent;
    const el = e.currentTarget;
    const next = previewAmountInputAfterBeforeInput(el, ne.inputType, ne.data ?? null);
    if (next === null) return;
    const frac = /\.(\d*)/.exec(next)?.[1] ?? '';
    if (frac.length > MAX_AMOUNT_FRACTION_DIGITS) {
      e.preventDefault();
      return;
    }
    if (amountStringWouldExceedMax(next, MAX_AMOUNT_INPUT)) {
      e.preventDefault();
    }
  };

  const handleAmountInputSanitize = (e: React.FormEvent<HTMLInputElement>) => {
    const el = e.currentTarget;
    const sanitized = sanitizeKhadhaAmountString(el.value);
    if (sanitized !== el.value) el.value = sanitized;

    const t = el.value.trim();
    let exceeds = false;
    if (t !== '' && t !== '-' && t !== '.') {
      if (/\.$/.test(t)) {
        const w = Number(t.slice(0, -1));
        exceeds = Number.isFinite(w) && w > MAX_AMOUNT_INPUT;
      } else {
        const n = Number(t);
        exceeds = Number.isFinite(n) && n > MAX_AMOUNT_INPUT;
      }
    }
    if (exceeds) {
      el.value = amountLastWithinMaxRef.current;
      return;
    }
    amountLastWithinMaxRef.current = el.value;
  };

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>, nextElement?: HTMLInputElement | HTMLButtonElement, isSave: boolean = false, validateCurrent?: () => boolean) => {
    if (event.key === 'Enter') {
      // For Enter key, validate current field before moving
      if (validateCurrent && !validateCurrent()) {
        // Field is invalid, prevent moving to next field
        event.preventDefault();
        return;
      }
      
      if (isSave) {
        await saveSodo();
      } 
      if (nextElement && typeof (nextElement as any).focus === 'function') {
        (nextElement as any).focus();
      }
      event.preventDefault();
    }
    // Tab and other keys are allowed to propagate normally (no validation check)
  };

  /**
   * Resolves an ALT+key shortcut to a team/side value (e.g. "A/K").
   * Returns empty string if the shortcut doesn't match any mapping.
   */
  const resolveAltShortcutToLkValue = (key: string): string => {
    if (!teamA || !teamB || !initialToTeamMap) return '';

    const teamAInitial = Object.keys(initialToTeamMap).find(k => initialToTeamMap[k] === teamA);
    const teamBInitial = Object.keys(initialToTeamMap).find(k => initialToTeamMap[k] === teamB);
    const isTestGame = gameType?.toLowerCase() === 'test';
    const k = key.toUpperCase();

    if (k === 'Z' && teamAInitial) return `${teamAInitial}/K`;
    if (k === 'X' && teamAInitial) return `${teamAInitial}/L`;
    if (k === 'C' && teamBInitial) return `${teamBInitial}/K`;
    if (k === 'V' && teamBInitial) return `${teamBInitial}/L`;
    if (isTestGame && k === 'B') return 'D/K';
    if (isTestGame && k === 'N') return 'D/L';

    return '';
  };

  /**
   * Parses and validates a raw LK string (e.g. "A/K"), sets state,
   * and moves focus to the amount input. Returns true if applied.
   */
  const applyLkValue = (valueToSet: string): boolean => {
    const raw = valueToSet.toUpperCase().replace('/', '');
    const first = raw[0] || '';
    const third = raw[1] || '';

    if (first && validFirsChar.length > 0 && !validFirsChar.includes(first)) return false;
    if (third && !['K', 'L'].includes(third)) return false;

    setFirstChar(first);
    setThirdChar(third);
    return true;
  };

  /** Validates the current LK input before allowing Enter to proceed. */
  const validateLkInput = (): boolean => {
    const gt = gameType?.toLowerCase();
    if (gt === 'session') {
      const digit = firstChar.replace(/[^0-9]/g, '');
      if (!digit) {
        setMessage('Please enter a valid digit for session game');
        return false;
      }
      return true;
    }
    if (!firstChar || !thirdChar) {
      setMessage('Please enter valid LK value (e.g., A/L, A/K)');
      return false;
    }
    return true;
  };

  /** Combined keyDown handler for the LK input field. */
  const handleLkInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.altKey && !e.ctrlKey && !e.shiftKey) {
      const value = resolveAltShortcutToLkValue(e.key);
      if (value) {
        e.preventDefault();
        e.stopPropagation();
        applyLkValue(value);
        return;
      }
    }

    // When the field is full (e.g. "A/K"), allow overwriting in-place:
    //  - Cursor at start (pos 0): typing a valid first char replaces it
    //  - Cursor at end or after "/": typing K or L replaces the second char
    if (!e.altKey && !e.ctrlKey && !e.metaKey && e.key.length === 1 && firstChar && thirdChar) {
      const typed = e.key.toUpperCase();
      const cursorPos = (e.target as HTMLInputElement).selectionStart ?? 0;

      if (cursorPos === 0 && validFirsChar.includes(typed)) {
        e.preventDefault();
        setFirstChar(typed);
        return;
      }

      if (cursorPos >= 1 && (typed === 'K' || typed === 'L')) {
        e.preventDefault();
        setThirdChar(typed);
        return;
      }
    }

    const isLastField = fieldSequence[fieldSequence.length - 1] === 'lk';
    if (isLastField && e.key === 'Enter') {
      e.preventDefault();
      if (!validateLkInput()) return;
      if (areAllFieldsValid()) void saveSodo();
      return;
    }
    handleKeyDown(e, getNextFieldRef('lk')!, false, validateLkInput);
  };

  const handleLkFocus = () => handleFocus('lk');
  const handleAmountFocus = () => handleFocus('amount');
  const handleRateFocus = () => handleFocus('rate');
  const handlePartyFocus = () => handleFocus('party');

  const handleAmountWheel = (ev: React.WheelEvent<HTMLInputElement>) => ev.preventDefault();

  /** Validates amount and delegates Enter to next field. */
  const handleAmountKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
      e.preventDefault();
      return;
    }
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      const el = amountInputRef.current;
      if (el) {
        const cur = Number(el.value);
        if (Number.isFinite(cur)) {
          const rawStep = el.step;
          const step = rawStep && rawStep !== 'any' ? Number(rawStep) : 1;
          const delta = e.key === 'ArrowUp' ? step : -step;
          const next = cur + delta;
          if (next > MAX_AMOUNT_INPUT || !Number.isFinite(next)) {
            e.preventDefault();
            return;
          }
        }
      }
    }
    const isAmountValid = () => {
      const amount = Number(amountInputRef.current?.value);
      const n = Number.isFinite(amount) ? amount : NaN;
      if (!Number.isFinite(n) || n <= 0) {
        setMessage('Please enter a valid amount (greater than 0)');
        return false;
      }
      if (n > MAX_AMOUNT_INPUT) {
        setMessage(`Maximum amount is ${MAX_AMOUNT_INPUT.toLocaleString(undefined, { maximumFractionDigits: 2 })}`);
        return false;
      }
      return true;
    };
    const isLastField = fieldSequence[fieldSequence.length - 1] === 'amount';
    if (isLastField && e.key === 'Enter') {
      e.preventDefault();
      if (!isAmountValid()) return;
      if (areAllFieldsValid()) void saveSodo();
      return;
    }
    handleKeyDown(e, getNextFieldRef('amount')!, false, isAmountValid);
  };

  /** Validates rate and delegates Enter to next field. */
  const handleRateKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const isRateValid = () => {
      const rate = Number(rateInputRef.current?.value) || 0;
      if (rate <= 0) {
        setMessage('Please enter a valid rate (greater than 0)');
        return false;
      }
      return true;
    };
    const isLastField = fieldSequence[fieldSequence.length - 1] === 'rate';
    if (isLastField && e.key === 'Enter') {
      e.preventDefault();
      if (!isRateValid()) return;
      if (areAllFieldsValid()) void saveSodo();
      return;
    }
    handleKeyDown(e, getNextFieldRef('rate')!, false, isRateValid);
  };

  /** Resets all fields and exits edit mode. */
  const handleCancelEdit = () => {
    setEditingSodaId(null);
    setFirstChar('');
    setThirdChar('');
    if (amountInputRef.current) amountInputRef.current.value = '';
    amountLastWithinMaxRef.current = '';
    if (rateInputRef.current) rateInputRef.current.value = '';
    setPartyCode('');
    setValidatedPartyCode('');
    setPartyValidation({ isValid: false, error: '', partyName: '', partyId: null });
    if (partyInputRef.current) partyInputRef.current.value = '';
    setMessage('');
    getFirstFieldRef().current?.focus();
  };

  const handleCloseErrorDialog = () => setShowErrorDialog(false);

  const handleFocus = (fieldName: string) => {
    switch (fieldName) {
      case 'lk':
        const combinations = validFirsChar.map(char => `${char}/L, ${char}/K`).join(', ');
        setMessage(`Valid combinations: ${combinations}`);
        break;
      case 'amount':
        setMessage(
          `Type 1000 = 1 Rs. Max amount ${MAX_AMOUNT_INPUT.toLocaleString(undefined, { maximumFractionDigits: 2 })}.`,
        );
        break;
      case 'rate':
        setMessage('Type Rate 50');
        break;
      case 'party':
        setMessage('Select party. Type party code to search');
        break;
      default:
        setMessage('');
    }
  };

  /** Explicit Save/Update: cancel deferred party-field Enter save so we never stack two saves (Enter path + button). */
  const handleSaveButtonActivate = () => {
    if (isGameOver) return;
    if (saveInFlightRef.current) return;
    if (partyEnterFlushTimeoutRef.current) {
      clearTimeout(partyEnterFlushTimeoutRef.current);
      partyEnterFlushTimeoutRef.current = null;
    }
    void saveSodo();
  };

  const handleSaveButtonKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    if (isGameOver || saveInFlightRef.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const saveSodo = async () => {
    if (saveInFlightRef.current) {
      return;
    }
    saveInFlightRef.current = true;
    setIsSavingSoda(true);
    try {
    const amount = Number(amountInputRef.current?.value) || 0;
    const rate = Number(rateInputRef.current?.value) || 0;

    if (!amount || !rate) {
      return;
    }
    if (amount > MAX_AMOUNT_INPUT || !Number.isFinite(amount)) {
      setMessage(`Maximum amount is ${MAX_AMOUNT_INPUT.toLocaleString(undefined, { maximumFractionDigits: 2 })}`);
      amountInputRef.current?.focus();
      return;
    }

    // If editing, use update API
    if (editingSodaId) {
      // Validate all fields when editing (same as creating)
      const gt = gameType?.toLowerCase();
      
      // Validate LK
      if (gt === 'session') {
        const digit = firstChar.replace(/[^0-9]/g, '');
        if (!digit) {
          setMessage('Please enter a valid digit for session game');
          lkInputRef.current?.focus();
          return;
        }
      } else {
        if (!firstChar || !thirdChar) {
          setMessage('Please enter valid LK value (e.g., A/L, A/K)');
          lkInputRef.current?.focus();
          return;
        }
      }

      // Validate party when editing
      if (!partyValidation.isValid || !partyValidation.partyId) {
        setMessage('Please enter a valid party code');
        partyInputRef.current?.focus();
        return;
      }

      const foundParty = parties.find(party => party.id === partyValidation.partyId);
      let partyIdToUse = partyValidation.partyId;
      
      if (!foundParty) {
        // Party not in local list, but validation passed, so use the validated partyId
        partyIdToUse = partyValidation.partyId;
      } else {
        partyIdToUse = foundParty.id;
      }

      // Build update payload with all necessary fields
      const payload: any = {
        Amount: amount,
        Rate: rate,
        PartyId: partyIdToUse,
        IsPartnershipOn: partnershipEnabled,
        IsCommissionOn: commissionEnabled,
        firstChar: firstChar.toUpperCase()
      };

      // Compute lk per rules: for oneday/test use second char (l/k), for over use a digit
      let lkValue: string | null = null;
      if (gt === 'session') {
        // Over game → lk is a digit (use the firstChar if digit)
        const digit = firstChar.replace(/[^0-9]/g, '');
        lkValue = digit ? digit : null;
      } else {
        // OneDay/Test → use second char only (l/k)
        lkValue = thirdChar ? thirdChar.toLowerCase() : null;
      }
      if (!lkValue) {
        alert('Invalid LK. For OneDay/Test use L/K; for Over provide a digit.');
        return;
      }
      payload.lk = lkValue;

      if (gt === 'oneday' || gt === 'test') {
        const teamName = resolveTeam(firstChar);
        if (!teamName && gt !== 'test') {
          alert('Unable to resolve team from initial.');
          return;
        }
        payload.firstChar = firstChar.toUpperCase();
        if (teamName) payload.team = teamName;
        if (gt === 'test' && firstChar.toUpperCase() === 'D') payload.team = 'DRAW';
      }

      if (gt === 'session') {
        // Over game: expect a 3-digit over entry; if not available, set to null (to be implemented)
        payload.over = null;
      }

      try {
        // Use the current partnership toggle state and include all fields
        const response = await apiService.put(apiRoutes.sodas.updateSoda(editingSodaId), payload);
        const updatedRows = normalizeSodaApiListResponse(response);
        if (updatedRows.length > 0) {
          onComplete(updatedRows as Sodo[]);
        }

        setEditingSodaId(null);
        setFirstChar('');
        setThirdChar('');
        amountInputRef.current!.value = '';
        amountLastWithinMaxRef.current = '';
        rateInputRef.current!.value = '';
        setPartyCode('');
        setValidatedPartyCode('');
        setPartyValidation({ isValid: false, error: '', partyName: '', partyId: null });
        if (partyInputRef.current) {
          partyInputRef.current.value = '';
        }

        if (onEditComplete) {
          onEditComplete();
        }

        setTimeout(() => getFirstFieldRef().current?.focus(), 100);
        return;
      } catch (error: any) {
        console.error('Error updating Sodo:', error);
        setErrorMessage('Failed to update Sodo');
        setShowErrorDialog(true);
        return;
      }
    }

    // Create new soda - validate party
    if (editOnly) {
      setMessage('Only edit is allowed here. Click an edit icon to modify a soda.');
      return;
    }
    if (!partyValidation.isValid || !partyValidation.partyId) {
      setMessage('Please enter a valid party code');
      partyInputRef.current?.focus();
      return;
    }

    const foundParty = parties.find(party => party.id === partyValidation.partyId);
    let partyIdToUse = partyValidation.partyId;
    
    if (!foundParty) {
      // Party not in local list, but validation passed, so use the validated partyId
      partyIdToUse = partyValidation.partyId;
    } else {
      partyIdToUse = foundParty.id;
    }

    // Build payload based on game type
    const payload: any = {
      gameId: Number(gameId),
      amount,
      rate,
      partyId: partyIdToUse,
      isPartnershipOn: partnershipEnabled,
      isCommissionOn: commissionEnabled,
      firstChar: firstChar.toUpperCase()
    };

    const gt = gameType?.toLowerCase();

    // Compute lk per rules: for oneday/test use second char (l/k), for over use a digit
    let lkValue: string | null = null;
    if (gt === 'session') {
      // Over game → lk is a digit (use the firstChar if digit)
      const digit = firstChar.replace(/[^0-9]/g, '');
      lkValue = digit ? digit : null;
    } else {
      // OneDay/Test → use second char only (l/k)
      lkValue = thirdChar ? thirdChar.toLowerCase() : null;
    }
    if (!lkValue) {
      alert('Invalid LK. For OneDay/Test use L/K; for Over provide a digit.');
      return;
    }
    payload.lk = lkValue;

    if (gt === 'oneday' || gt === 'test') {
      const teamName = resolveTeam(firstChar);
      if (!teamName && gt !== 'test') {
        alert('Unable to resolve team from initial.');
        return;
      }
      payload.firstChar = firstChar.toUpperCase();
      if (teamName) payload.team = teamName;
      if (gt === 'test' && firstChar.toUpperCase() === 'D') payload.team = 'DRAW';
    }

    if (gt === 'session') {
      // Over game: expect a 3-digit over entry; if not available, set to null (to be implemented)
      payload.over = null;
    }

    try {
      const response = await apiService.post(apiRoutes.sodas.create, payload);
      const sodas = normalizeSodaApiListResponse(response);
      if (sodas.length > 0) {
        onComplete(sodas as Sodo[]);
      }
    } catch (error: any) {
      console.error('Error saving Sodo:', error);
      setErrorMessage('Failed to save Sodo');
      setShowErrorDialog(true);
      return;
    }

    setFirstChar('');
    setThirdChar('');
    amountInputRef.current!.value = '';
    amountLastWithinMaxRef.current = '';
    rateInputRef.current!.value = '';
    setPartyCode('');
    setValidatedPartyCode('');
    setPartyValidation({ isValid: false, error: '', partyName: '', partyId: null });
    if (partyInputRef.current) {
      partyInputRef.current.value = '';
    }
    setTimeout(() => {
      getFirstFieldRef().current?.focus();
    }, 100);
    } finally {
      saveInFlightRef.current = false;
      setIsSavingSoda(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.toUpperCase().replace('/', '');
    let first = raw[0] || '';
    let third = '';

    // When both chars already filled and user types a third char,
    // treat it as replacing the second char (K<->L) rather than appending.
    if (raw.length >= 2) {
      const lastChar = raw[raw.length - 1];
      if (validSecondChar.includes(lastChar)) {
        third = lastChar;
      } else {
        third = raw[1] || '';
      }
    } else {
      third = raw[1] || '';
    }

    if (first && !validFirsChar.includes(first)) {
      return;
    }

    if (third && !validSecondChar.includes(third)) {
      return;
    }

    setFirstChar(first);
    setThirdChar(third);
  };

  const handleMatchResultsClick = () => {
    if (isGameOver && gameDetails) {
      setShowMatchResults(true);
    }
  };

  return (
    <>
      {/* Match Results Dialog */}
      {gameDetails && (
        <MatchResultsDialog
          isOpen={showMatchResults}
          onClose={() => setShowMatchResults(false)}
          gameDetails={gameDetails}
        />
      )}

      {/* Error Dialog */}
      <DialogBox
        isOpen={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title="Error"
        panelClassName="max-w-md"
      >
        <div className="p-6">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {errorMessage}
              </h3>
              <div className="flex justify-end mt-4">
                <Button
                  onClick={handleCloseErrorDialog}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  OK
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogBox>

      <div className="w-full bg-gray-800">
        <div ref={barRef} className="flex flex-wrap items-center gap-1.5 md:gap-2 w-full border-spacing-1 p-1.5 md:p-1 bg-gray-800 border-t border-gray-200 shadow-sm">
        <strong className="text-white font-semibold text-xs md:text-base">SODA:</strong>
        {fieldSequence.map((fieldKey) => {
          switch (fieldKey) {
            case 'lk':
              return (
                <input
                  key="lk"
                  type="text"
                  id="soda-lk-input"
                  ref={lkInputRef}
                  value={`${firstChar}/${thirdChar}`}
                  onChange={handleChange}
                  onInput={handleChange}
                  onKeyDown={handleLkInputKeyDown}
                  onFocus={handleLkFocus}
                  maxLength={3}
                  disabled={isGameOver || isSavingSoda}
                  className={`border border-blue-300/50 w-16 sm:w-20 md:w-24 text-center uppercase rounded-lg bg-white shadow-sm text-sm md:text-base font-bold focus:ring-2 focus:ring-blue-400 focus:border-blue-500 transition-all duration-150 ${getTextColorClass(thirdChar)} ${isGameOver || isSavingSoda ? 'bg-gray-300 cursor-not-allowed' : ''}`}
                />
              );
            case 'amount':
              return (
                <input
                  key="amount"
                  ref={amountInputRef}
                  type="number"
                  onKeyDown={handleAmountKeyDown}
                  onBeforeInput={handleAmountBeforeInput}
                  onInput={handleAmountInputSanitize}
                  onChange={handleAmountInputSanitize}
                  onWheel={handleAmountWheel}
                  onFocus={handleAmountFocus}
                  disabled={isGameOver || isSavingSoda}
                  min={0.01}
                  max={MAX_AMOUNT_INPUT}
                  step="any"
                  className={`border border-blue-300/50 w-16 sm:w-20 md:w-24 px-1.5 md:px-2 py-0.5 md:py-0 rounded-lg bg-white text-black font-bold text-sm md:text-base shadow-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-500 transition-all duration-150 ${isGameOver || isSavingSoda ? 'bg-gray-300 cursor-not-allowed' : ''}`}
                  placeholder="Amount"
                />
              );
            case 'rate':
              return (
                <input
                  key="rate"
                  ref={rateInputRef}
                  type="number"
                  onKeyDown={handleRateKeyDown}
                  onFocus={handleRateFocus}
                  disabled={isGameOver || isSavingSoda}
                  className={`border border-blue-300/50 w-16 sm:w-20 md:w-24 px-1.5 md:px-2 py-0.5 md:py-0 rounded-lg bg-white text-black font-bold text-sm md:text-base shadow-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-500 transition-all duration-150 ${isGameOver || isSavingSoda ? 'bg-gray-300 cursor-not-allowed' : ''}`}
                  placeholder="Rate"
                />
              );
            case 'party':
              return (
                <div key="party" className={`min-w-0 flex-1 md:flex-none md:min-w-[200px] md:w-64 ${isGameOver || isSavingSoda ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input
                    ref={partyInputRef}
                    type="text"
                    onChange={handlePartyCodeChange}
                    onBlur={handlePartyCodeBlur}
                    onKeyDown={handlePartyCodeEnter}
                    onFocus={handlePartyFocus}
                    disabled={isGameOver || isSavingSoda}
                    maxLength={50}
                    placeholder="Party Code"
                    className={`border w-full px-1.5 md:px-2 py-0.5 md:py-0 rounded-lg bg-white text-black font-bold text-sm md:text-base shadow-sm focus:ring-2 transition-all duration-150 ${isGameOver || isSavingSoda ? 'bg-gray-300 cursor-not-allowed' : ''} ${partyValidation.error ? 'border-red-500 focus:ring-red-400' : partyValidation.isValid ? 'border-green-500 focus:ring-green-400' : 'border-blue-300/50 focus:ring-blue-400 focus:border-blue-500'}`}
                  />
                  {partyValidation.error && (
                    <div className="text-red-600 text-xs mt-1 font-medium">{partyValidation.error}</div>
                  )}
                </div>
              );
            default:
              return null;
          }
        })}

        {editingSodaId && (
          <button
            onClick={handleCancelEdit}
            disabled={isGameOver || isSavingSoda}
            className={`px-2 md:px-3 py-0.5 md:py-1 text-xs md:text-base rounded-lg bg-gradient-to-r from-gray-500 to-gray-600 text-white hover:from-gray-600 hover:to-gray-700 shadow-md transition-all duration-150 font-semibold ${isGameOver || isSavingSoda ? 'bg-gray-400 cursor-not-allowed text-gray-600' : ''}`}>
              Cancel
            </button>
        )}
        <button
          type="button"
          ref={saveButtonRef}
          onClick={handleSaveButtonActivate}
          onKeyDown={handleSaveButtonKeyDown}
          disabled={isGameOver || isSavingSoda}
          className={`px-2 md:px-3 py-0.5 md:py-1 text-xs md:text-base rounded-lg font-semibold shadow-md transition-all duration-150 ${isGameOver || isSavingSoda ? 'bg-gray-400 cursor-not-allowed text-gray-600' : editingSodaId ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700' : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700'}`}>
            {editingSodaId ? 'Update' : 'Save'}
        </button>

          {isGameOver ? (
            <button
              onClick={handleMatchResultsClick}
              className="flex items-center gap-1.5 px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-lg ml-auto transition-all duration-200 cursor-pointer shadow-md hover:shadow-lg font-semibold"
              title="View Match Results"
            >
              <TrophyIcon className="h-4 w-4 md:h-5 md:w-5" />
              Match Completed
            </button>
          ) : !editOnly ? (
            <button
              onClick={onMatchOver}
              className="px-2 md:px-3 py-0.5 md:py-1 text-xs md:text-base bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg ml-auto transition-all duration-150 shadow-md font-semibold"
              title="Complete Match"
            >
              Match Over
            </button>
          ) : null}
      </div>
        <div className="w-full pt-0 pb-1 px-1 bg-white text-slate-700 text-xs md:text-sm text-center min-h-[24px] flex items-center justify-center border-t border-gray-200">
            {message || 'Messages will appear here'}
        </div>
      </div>
    </>
  );
};

export default LagayaaKhadhaComponent;
